import { WireObject } from 'content-creation-common/models';

import UserContext from '../UserContext';
import { isEmptyString, isNil, isNotNil } from '../../../util/object';
import CreativeContentItemWireObject from './CreativeContentItemWireObject';
import { isEmpty } from '../../../util/array';

export default class CreativeContentWireObject extends WireObject {
  _contentItems = null;
  _id = null;
  _userContext = null;
  _isFilled = false;

  /**
   * @param {UserContext} userContext
   * @param {Map|null} contentItems
   */
  constructor(userContext, contentItems) {
    super();

    if (!UserContext.isUserContext(userContext)) {
      throw new Error('Must provide value user context');
    }

    this._userContext = userContext;
    this._contentItems = new Map(contentItems);
  }

  /**
   * Add a contentItem to this CreativeContent object
   *
   * @param {String} key
   * @param {CreativeContentItemWireObject} contentItem
   */
  addContentItem(key, contentItem) {
    if (isEmptyString(key)) {
      throw new Error('Invalid key, must be string');
    }

    if (
      !CreativeContentItemWireObject.isCreativeContentItemWireObject(
        contentItem,
      )
    ) {
      throw new Error(
        'Can only add CreativeContentItems to CreativeContentWireObject',
      );
    }
    this._contentItems.set(key, contentItem);
  }

  /**
   * return {CreativeContentWireObject} new CreativeContentWireObject without an ID, based on this one.
   */
  copy() {
    const newCreativeContentWireObject = new CreativeContentWireObject(
      this.userContext.copy(),
      this.contentItems,
    );
    newCreativeContentWireObject.isFilled = this.isFilled;
    return newCreativeContentWireObject;
  }

  /**
   * Remove content item by key
   * @param {string} key
   */
  removeContentItem(key) {
    if (!this._contentItems.has('key')) return;
    this._contentItems.delete(key);
  }

  toJSON() {
    const userContextWireObject = this.userContext.toJSON();

    const json = Object.assign(
      {
        is_filled: this.isFilled,
      },
      userContextWireObject,
    );

    if (isNotNil(this.id)) {
      json.id = this.id;
    }

    if (this.contentItems.size > 0) {
      const contentItemEntries = {};
      this.contentItems.forEach((value, key) => {
        contentItemEntries[key] = value;
      });

      json.creative_content_rows = [
        {
          creative_content_items: contentItemEntries,
        },
      ];
    }

    return json;
  }

  get contentItems() {
    return this._contentItems;
  }

  get id() {
    return this._id;
  }

  set id(value) {
    this._id = value;
  }

  get isFilled() {
    return this._isFilled;
  }

  set isFilled(value) {
    this._isFilled = value;
  }

  get userContext() {
    return this._userContext;
  }

  static isCreativeContentWireObject(incoming) {
    return incoming instanceof CreativeContentWireObject;
  }

  /**
   * Turn a JSON object in to the CreativeContentWireObject model
   * @param inJSON
   * @returns {CreativeContentWireObject}
   */
  static fromJSON(inJSON) {
    if (isNil(inJSON)) {
      throw new Error('CreativeContentWireObject.fromJSON inJSON is nil');
    }

    const {
      id,
      creative_content_rows: creativeContentRows,
      is_filled: isFilled,
    } = inJSON;

    const userContext = UserContext.fromJSON(inJSON);
    const newCreativeContentWireObject = new CreativeContentWireObject(
      userContext,
      null,
    );
    newCreativeContentWireObject.id = id;
    newCreativeContentWireObject.isFilled = Boolean(isFilled);

    if (!isEmpty(creativeContentRows)) {
      creativeContentRows.forEach((row) => {
        const { creative_content_items: creativeContentItems } = row;
        if (isNil(creativeContentItems)) return;

        const keys = Object.keys(creativeContentItems);
        if (isEmpty(keys)) return;

        keys.forEach((key) => {
          const creativeContentItem = CreativeContentItemWireObject.fromJSON(
            creativeContentItems[key],
          );
          newCreativeContentWireObject.addContentItem(key, creativeContentItem);
        });
      });
    }

    return newCreativeContentWireObject;
  }
}
